import React, { useState, useEffect } from 'react';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { api } from '../../api/nocode';

function isSkillplateTestLog(log) {
  const details = String(log?.error_message || '').toLowerCase();
  if (details.includes('skillplate test')) return true;

  try {
    const payload = JSON.parse(log?.payload || '{}');
    const eventType = String(
      payload?.event ||
      payload?.event_type ||
      payload?.type ||
      payload?.webhook_type ||
      payload?.action ||
      payload?.data?.event ||
      payload?.data?.event_type ||
      payload?.data?.type ||
      payload?.payload?.event ||
      payload?.payload?.type ||
      ''
    ).toLowerCase();

    return (
      ['test', 'webhook.test', 'webhook_test', 'ping'].includes(eventType) ||
      Boolean(payload?.test) ||
      Boolean(payload?.is_test)
    );
  } catch (e) {
    return false;
  }
}

function formatWebhookTimestamp(value) {
  if (!value) return 'Timestamp unavailable';

  const timestamp = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(timestamp.getTime())
    ? String(value)
    : timestamp.toLocaleString();
}

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [latestWebhookTest, setLatestWebhookTest] = useState(null);
  const [webhookTestLoading, setWebhookTestLoading] = useState(false);
  const [webhookTestError, setWebhookTestError] = useState('');

  const CRON_SECRET = 'Cek25yUNkCKEnnq1QVTqK3aceALH0MGY';

  const regions = [
    'us-east-2', 'us-east-1', 'us-west-1', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-central-1',
    'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1'
  ];

  const loadSettings = async () => {
    try {
      const res = await api.get('settings_gwaacademy');
      const data = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setOriginalSettings(data);
      const map = {};
      data.forEach(item => {
        if (item.setting_key) map[item.setting_key] = item.value;
      });
      // Ensure default webhook secret exists visually
      if (!map.webhook_secret) map.webhook_secret = 'sk_' + Math.random().toString(36).substring(2, 15);
      if (!map.cron_secret) map.cron_secret = CRON_SECRET;
      setSettings(map);
    } catch (e) {
      console.error("Failed to load settings", e);
    }
  };

  const loadLatestWebhookTest = async () => {
    setWebhookTestLoading(true);
    setWebhookTestError('');

    try {
      const res = await api.getUnscoped('webhook_logs_gwaacademy', {
        sort: 'created_at',
        order: 'desc',
        limit: 100
      });
      const data = Array.isArray(res?.data) ? res.data : [];
      const matchingLogs = data
        .filter(isSkillplateTestLog)
        .sort((a, b) => {
          const dateDiff = String(b?.created_at || '').localeCompare(String(a?.created_at || ''));
          if (dateDiff !== 0) return dateDiff;
          const aIsReceived = String(a?.status || '').toLowerCase() === 'received';
          const bIsReceived = String(b?.status || '').toLowerCase() === 'received';
          if (aIsReceived && !bIsReceived) return 1;
          if (!aIsReceived && bIsReceived) return -1;
          return Number(b?.id || 0) - Number(a?.id || 0);
        });

      setLatestWebhookTest(matchingLogs[0] || null);
    } catch (e) {
      console.error("Failed to load webhook test result", e);
      setWebhookTestError(e.message || 'Unable to load the latest test result.');
    } finally {
      setWebhookTestLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadLatestWebhookTest();
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setToast('');
    try {
      const promises = [];
      for (const [key, value] of Object.entries(settings)) {
        const orig = originalSettings.find(s => s.setting_key === key);
        const strValue = value === null || value === undefined ? '' : String(value);
        if (orig) {
          if (orig.value !== strValue) {
            promises.push(api.put('settings_gwaacademy', orig.id, { setting_key: key, value: strValue, owner_id: 'admin' }));
          }
        } else {
          if (strValue !== '') {
            // Do not provide client-generated id; NCB auto-assigns integer id
            promises.push(api.post('settings_gwaacademy', { setting_key: key, value: strValue, owner_id: 'admin' }));
          }
        }
      }
      await Promise.all(promises);
      setToast('Settings saved successfully!');
      await loadSettings(); 
    } catch (e) {
      console.error("Failed to save settings:", e);
      setToast('Error saving settings: ' + (e.message || 'Please try again.'));
    } finally {
      setSaving(false);
      setTimeout(() => setToast(''), 3000);
    }
  };

  const webhookTestSucceeded =
    String(latestWebhookTest?.status || '').toLowerCase() === 'success';

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Platform Settings</h1>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>

      {toast && (
        <div className={`px-4 py-3 rounded border ${toast.includes('Error') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'}`}>
          {toast}
        </div>
      )}

      {/* REGISTRATION TOGGLE */}
      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Users" /> Public Registration
        </h2>
        <div className="p-4 border rounded-md bg-gray-50 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-dark-green">Enable Registration</h4>
            <p className="text-xs text-muted">When OFF, the registration page will show a closed message and redirect to the waitlist.</p>
          </div>
          <Button 
            variant={settings.registration_open !== 'false' ? 'primary' : 'outline'}
            onClick={() => handleChange('registration_open', settings.registration_open === 'false' ? 'true' : 'false')}
            className={settings.registration_open !== 'false' ? 'bg-forest-green text-white border-forest-green' : 'text-gray-500'}
          >
            {settings.registration_open !== 'false' ? 'ON' : 'OFF'}
          </Button>
        </div>
      </Card>

      {/* LETTERS TOGGLE */}
      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Mail" /> Self-Letter Writing Module
        </h2>
        <div className="p-4 border rounded-md bg-gray-50 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-dark-green">Enable Self-Letter Module</h4>
            <p className="text-xs text-muted">When OFF, the letters module will be locked for all campers.</p>
          </div>
          <Button 
            variant={settings.letters_module_open !== 'false' ? 'primary' : 'outline'}
            onClick={() => handleChange('letters_module_open', settings.letters_module_open === 'false' ? 'true' : 'false')}
            className={settings.letters_module_open !== 'false' ? 'bg-forest-green text-white border-forest-green' : 'text-gray-500'}
          >
            {settings.letters_module_open !== 'false' ? 'ON' : 'OFF'}
          </Button>
        </div>
      </Card>

      {/* NAMEHERO / CPANEL CRON JOB CONFIGURATION */}
      <Card className="space-y-6">
        <div className="border-b pb-2 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon name="Clock" /> Automated Letter Delivery (NameHero cPanel Cron)
          </h2>
          <span className="text-xs font-mono font-semibold bg-gwa-blue/10 text-gwa-blue px-2.5 py-1 rounded border border-gwa-blue/30">
            Interval: */5 * * * *
          </span>
        </div>

        <div className="space-y-4">
          <Input 
            label="Cron Authentication Secret" 
            type="text" 
            value={settings.cron_secret || CRON_SECRET} 
            onChange={e => handleChange('cron_secret', e.target.value)} 
            className="font-mono text-sm" 
          />

          <Input 
            label="Cron Job URL (Browser Test)" 
            value={`https://assets.emmausway.co/gwa_academy/cron-letters.php?key=${settings.cron_secret || CRON_SECRET}`} 
            readOnly 
            className="bg-gray-50 font-mono text-xs" 
          />

          <div className="rounded-md border border-forest-green/30 bg-forest-green/5 p-4 text-xs text-dark-green space-y-2">
            <p className="font-bold text-forest-green text-sm flex items-center gap-1.5">
              <SafeIcon name="Terminal" /> NameHero cPanel Setup Instructions:
            </p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Log into your <b>NameHero cPanel</b> &rarr; scroll to <b>Advanced</b> &rarr; click <b>Cron Jobs</b>.</li>
              <li>Under <i>Add New Cron Job</i>, select <b>Once Per 5 Minutes (* / 5 * * * *)</b> from Common Settings.</li>
              <li>In the <b>Command</b> input field, paste the exact command below:</li>
            </ol>
            <div className="bg-gray-900 text-emerald-400 font-mono p-3 rounded text-xs select-all overflow-x-auto">
              curl -s "https://assets.emmausway.co/gwa_academy/cron-letters.php?key={settings.cron_secret || CRON_SECRET}" &gt; /dev/null
            </div>
            <p className="text-muted text-[11px] pt-1">
              The cron runs every 5 minutes, checks for scheduled letters whose delivery timestamp (UTC) has arrived, verifies against duplicates, and sends them via AWS SES (gwaacademy-ses).
            </p>
          </div>
        </div>
      </Card>

      {/* DASHBOARD MESSAGES */}
      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="MessageSquare" /> Dashboard Messages
        </h2>
        <div className="space-y-4">
          <Textarea 
            label="Camp Closed Message (All Campers)" 
            value={settings.camp_closed_msg || ''} 
            onChange={e => handleChange('camp_closed_msg', e.target.value)} 
            placeholder="Message displayed to all campers after camp ends, until replay expires."
          />
          <Textarea 
            label="All-Access Welcome Message (Post-Camp)" 
            value={settings.aa_welcome_msg || ''} 
            onChange={e => handleChange('aa_welcome_msg', e.target.value)} 
            placeholder="Message displayed to All-Access members after camp ends if 'Camp Closed Message' is empty."
          />
          <Textarea 
            label="Replay Expiry Message (Lockout Screen)" 
            value={settings.replay_expiry_msg || ''} 
            onChange={e => handleChange('replay_expiry_msg', e.target.value)} 
            placeholder="Message displayed to free pass users when replay access has expired."
          />
        </div>
      </Card>

      {/* AWS SES CONFIGURATION */}
      <Card className="space-y-6">
        <div className="border-b pb-2 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon name="Mail" /> AWS SES Configuration
          </h2>
          <span className="text-xs font-mono font-semibold bg-forest-green/10 text-forest-green px-2.5 py-1 rounded border border-forest-green/30">
            IAM User: gwaacademy-ses
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Input 
            label="AWS Access Key ID (gwaacademy-ses)" 
            type="text" 
            value={settings.ses_access_key || ''} 
            onChange={e => handleChange('ses_access_key', e.target.value)} 
            placeholder="AKIA..."
          />
          <Input 
            label="AWS Secret Access Key" 
            type="password" 
            value={settings.ses_secret_key || ''} 
            onChange={e => handleChange('ses_secret_key', e.target.value)} 
            placeholder="••••••••••••••••••••••••••••••••"
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-dark-green">AWS SES Region</label>
            <select
              className="px-4 py-2 rounded-md border border-amber bg-cream focus:outline-none focus:ring-2 focus:ring-forest-green"
              value={settings.ses_region || 'us-east-2'}
              onChange={e => handleChange('ses_region', e.target.value)}
            >
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <Input 
            label="From Email Address" 
            value={settings.ses_from_email || ''} 
            onChange={e => handleChange('ses_from_email', e.target.value)} 
            placeholder="team@gospelwiseauthor.com"
          />
          <Input 
            label="Display Name" 
            value={settings.ses_display_name || ''} 
            onChange={e => handleChange('ses_display_name', e.target.value)} 
            placeholder="GospelWise Author Academy"
          />
        </div>

        <div className="rounded-md border border-forest-green/30 bg-forest-green/5 p-4 text-xs text-dark-green space-y-1">
          <p className="font-semibold text-forest-green">IAM User Verification Note:</p>
          <p>
            The Access Key ID and Secret Access Key configured above belong to the <span className="font-mono font-bold text-forest-green">gwaacademy-ses</span> IAM user in AWS. Ensure that this user has active <span className="font-mono">ses:SendEmail</span> permissions and that your sender domain or email (<span className="font-mono">{settings.ses_from_email || 'team@gospelwiseauthor.com'}</span>) is verified in SES region <span className="font-mono font-bold">{settings.ses_region || 'us-east-2'}</span>.
          </p>
        </div>
      </Card>

      {/* SKILLPLATE WEBHOOK CONFIGURATION */}
      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Link" /> Skillplate Webhook Configuration
        </h2>
        <div className="space-y-4">
          <Input label="Webhook Secret Key" type="text" value={settings.webhook_secret || ''} onChange={e => handleChange('webhook_secret', e.target.value)} className="font-mono text-sm" />
          <Input 
            label="Global Webhook URL (Copy to Skillplate)" 
            value={`https://assets.emmausway.co/gwa_academy/webhook-receiver.php?secret=${settings.webhook_secret || ''}`} 
            readOnly 
            className="bg-gray-50 font-mono text-xs" 
          />
          <div className="rounded-md border border-amber/40 bg-amber/10 p-4 text-sm text-dark-green">
            The webhook URL remains global. Configure each session’s Student
            Access Product and All-Access Product from Learning Centers &amp;
            Sessions. Incoming product names are matched to those session-level
            values.
          </div>
        </div>
      </Card>

      <Card className="space-y-4">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <SafeIcon name="Activity" /> Latest Skillplate Webhook Test
            </h2>
            <p className="mt-1 text-xs text-muted">
              Send a test from Skillplate, then refresh to confirm the response and saved log.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={loadLatestWebhookTest}
            disabled={webhookTestLoading}
          >
            <SafeIcon name="RefreshCw" className={webhookTestLoading ? 'animate-spin' : ''} />
            {webhookTestLoading ? 'Checking...' : 'Refresh Result'}
          </Button>
        </div>

        {webhookTestError && (
          <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700">
            {webhookTestError}
          </div>
        )}

        {!webhookTestLoading && !webhookTestError && !latestWebhookTest && (
          <div className="rounded-md border border-tan bg-gray-50 p-4 text-sm text-muted">
            No Skillplate webhook test has been recorded yet.
          </div>
        )}

        {latestWebhookTest && (
          <div className={`rounded-md border p-4 ${
            webhookTestSucceeded
              ? 'border-emerald-300 bg-emerald-50'
              : 'border-red-300 bg-red-50'
          }`}>
            <div className="flex items-start gap-3">
              <SafeIcon
                name={webhookTestSucceeded ? 'CheckCircle' : 'AlertCircle'}
                className={`mt-0.5 text-xl ${
                  webhookTestSucceeded ? 'text-emerald-700' : 'text-red-700'
                }`}
              />
              <div>
                <div className={`font-bold ${
                  webhookTestSucceeded ? 'text-emerald-800' : 'text-red-800'
                }`}>
                  {webhookTestSucceeded
                    ? 'HTTP 200 test succeeded'
                    : 'Webhook test failed'}
                </div>
                <div className="mt-1 text-xs text-gray-600">
                  {formatWebhookTimestamp(latestWebhookTest?.created_at)}
                </div>
                <div className="mt-3 whitespace-pre-wrap break-words text-sm text-gray-700">
                  {latestWebhookTest?.error_message || 'No error details were recorded.'}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Calendar" /> Camp Dates Configuration
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Camp Start Date" type="date" value={settings.camp_start || ''} onChange={e => handleChange('camp_start', e.target.value)} />
          <Input label="Camp End Date" type="date" value={settings.camp_end || ''} onChange={e => handleChange('camp_end', e.target.value)} />
          <Input label="Early Bird Deadline" type="datetime-local" value={settings.early_bird_deadline || ''} onChange={e => handleChange('early_bird_deadline', e.target.value)} />
          <Input label="Replay Expiry (Live Camp)" type="datetime-local" value={settings.replay_expiry || ''} onChange={e => handleChange('replay_expiry', e.target.value)} />
          <Input label="Evergreen Replay Window (Days)" type="number" value={settings.evergreen_replay_days || '14'} onChange={e => handleChange('evergreen_replay_days', e.target.value)} />
        </div>
      </Card>
    </div>
  );
}